import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { useInView } from "framer-motion";
import { useRef } from "react";
import styled from "styled-components";

interface AsideCardProps {
  title?: string | React.ReactNode;
  style?: React.CSSProperties;
  useAlternateStyle?: boolean;
  bgcolor?: "light" | "dark";
  animateInView?: boolean;
  showDivider?: boolean;
  chipText?: string;
  content: string[];
  flex?: number;
}

const AsideCard = ({
  flex = 1,
  style,
  title,
  content,
  chipText,
  bgcolor = "light",
  showDivider = false,
  animateInView = false,
  useAlternateStyle = false,
}: AsideCardProps) => {
  const theme = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: "some" });

  return (
    <AsideCardWrapper ref={ref} style={{ ...style, flex }}>
      <Stack
        className="aside-card-stack"
        justifyContent={"start"}
        direction={"column"}
        alignItems={"start"}
        gap={1.5}
        style={{
          minHeight: style?.minHeight,
          transform: !animateInView
            ? "none"
            : isInView
            ? "none"
            : "translateX(-200px)",
          opacity: !animateInView ? 1 : isInView ? 1 : 0,
          transition: "all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s",
        }}
      >
        {title && (
          <Typography
            variant="h4"
            fontWeight="bold"
            color={
              bgcolor === "light"
                ? theme.palette.primary.main
                : theme.palette.common.white
            }
          >
            {title}
          </Typography>
        )}

        <Stack
          style={{
            backgroundColor: chipText
              ? useAlternateStyle
                ? `${theme.palette.info.main}22`
                : "rgba(255,255,255,0.05)"
              : "inherit",
            border: useAlternateStyle
              ? `0.065rem solid ${theme.palette.info.main}`
              : "none",
            padding: useAlternateStyle
              ? "1.2rem 0.8rem 1.2rem 0"
              : chipText
              ? "0.8rem 0.8rem 0.6rem 0"
              : "inherit",
            borderRadius: useAlternateStyle
              ? "8px"
              : chipText
              ? "0 4px 4px 0"
              : "inherit",
            height: "100%",
          }}
          justifyContent={"start"}
          position={"relative"}
          alignItems={"start"}
          direction={"row"}
          gap={2}
        >
          {showDivider && !useAlternateStyle && (
            <Box
              component="div"
              sx={{
                background: theme.palette.yellow.main,
                minHeight: style?.minHeight,
              }}
              position="absolute"
              height={"100%"}
              width="0.125rem"
              left={0}
              top={0}
            />
          )}

          <Stack
            pl={showDivider ? 2.5 : 0}
            justifyContent={"start"}
            gap={!title ? 1.5 : 3}
            direction={"column"}
            alignItems={"start"}
          >
            {content.map((text, index) => (
              <Typography
                component="div"
                style={{
                  fontWeight:
                    content.length - 1 === index && !title ? "200" : "500",
                }}
                variant={
                  content.length - 1 === index && !title
                    ? "body1"
                    : "textSMMedium"
                }
                color={
                  bgcolor === "light"
                    ? theme.palette.primary.main
                    : theme.palette.common.white
                }
                key={index}
              >
                <Stack
                  component="div"
                  justifyContent={"start"}
                  alignItems={"center"}
                  direction={"row"}
                  gap={1.75}
                >
                  {chipText && index === 0 && (
                    <Chip
                      label={
                        <span dangerouslySetInnerHTML={{ __html: chipText }} />
                      }
                      variant="outlined"
                      sx={{
                        borderRadius: "4px",
                        pointerEvents: "none",
                        textTransform: "uppercase",
                        color: useAlternateStyle
                          ? theme.palette.common.white
                          : theme.palette.yellow.main,
                        borderColor: useAlternateStyle
                          ? theme.palette.info.main
                          : theme.palette.yellow.main,
                        backgroundColor: useAlternateStyle
                          ? `${theme.palette.common.white}13`
                          : `${theme.palette.yellow.main}1A`,
                      }}
                    />
                  )}
                  <span dangerouslySetInnerHTML={{ __html: text }} />
                </Stack>
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </AsideCardWrapper>
  );
};

export default AsideCard;

const AsideCardWrapper = styled.div`
  @media (max-width: 600px) {
    .aside-card-stack {
      min-height: 5.5rem !important;

      .MuiBox-root {
        min-height: 6rem !important;
      }
    }
  }
`;
